import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowRight, Loader2, Check, Plus, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = [
  { id: "daily", label: "Daily Cleaning", note: "~22 visits / month" },
  { id: "weekly", label: "Weekly Cleaning", note: "4 visits / month" },
  { id: "biweekly", label: "Bi-Weekly Cleaning", note: "2 visits / month" },
];

const Stepper = ({ label, value, setValue, testid }) => (
  <div className="bg-white border-2 border-slate-200 rounded-sm p-4">
    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">{label}</p>
    <div className="flex items-center justify-between">
      <button
        type="button"
        data-testid={`${testid}-minus`}
        onClick={() => setValue((v) => Math.max(0, v - 1))}
        className="w-9 h-9 flex items-center justify-center border-2 border-slate-200 rounded-sm text-[#1c4f9c] hover:border-[#1c4f9c] transition-colors"
        aria-label={`Decrease ${label}`}
      >
        <Minus size={16} strokeWidth={2.5} />
      </button>
      <span data-testid={`${testid}-value`} className="text-2xl font-extrabold font-display text-[#1c4f9c]">{value}</span>
      <button
        type="button"
        data-testid={`${testid}-plus`}
        onClick={() => setValue((v) => v + 1)}
        className="w-9 h-9 flex items-center justify-center border-2 border-slate-200 rounded-sm text-[#1c4f9c] hover:border-[#1c4f9c] transition-colors"
        aria-label={`Increase ${label}`}
      >
        <Plus size={16} strokeWidth={2.5} />
      </button>
    </div>
  </div>
);

export const PricingCalculator = () => {
  const [sqft, setSqft] = useState(5000);
  const [frequency, setFrequency] = useState("weekly");
  const [includeSupplies, setIncludeSupplies] = useState(false);
  const [bathrooms, setBathrooms] = useState(2);
  const [dispensers, setDispensers] = useState(4);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/estimate`, {
        square_footage: Number(sqft),
        frequency,
        include_supplies: includeSupplies,
        bathrooms: Number(bathrooms),
        dispensers: Number(dispensers),
      });
      setResult(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [sqft, frequency, includeSupplies, bathrooms, dispensers]);

  useEffect(() => {
    const t = setTimeout(calculate, 250);
    return () => clearTimeout(t);
  }, [calculate]);

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 sm:py-32 bg-[#f1f5f9]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e02329] mb-3">Instant Estimate</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1c4f9c]">
            Get an instant cleaning estimate
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Adjust your facility size and cleaning frequency to see an estimated monthly cost. Final pricing confirmed after a walkthrough.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-sm p-8 sm:p-10">
            <label className="block text-sm font-bold text-[#1c4f9c] mb-4">Select Cleaning Frequency</label>
            <div className="grid sm:grid-cols-3 gap-3 mb-10">
              {FREQUENCIES.map((f) => (
                <button
                  key={f.id}
                  data-testid={`frequency-${f.id}`}
                  onClick={() => setFrequency(f.id)}
                  className={`text-left p-4 rounded-sm border-2 transition-colors duration-200 ${
                    frequency === f.id
                      ? "border-[#1c4f9c] bg-[#1c4f9c]/5"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className={`block text-sm font-bold ${frequency === f.id ? "text-[#1c4f9c]" : "text-slate-700"}`}>{f.label}</span>
                  <span className="block text-xs text-slate-500 mt-1">{f.note}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-bold text-[#1c4f9c]">Facility Square Footage</label>
              <span data-testid="sqft-value" className="text-lg font-extrabold font-display text-[#e02329]">{Number(sqft).toLocaleString()} sq ft</span>
            </div>
            <input
              type="range"
              min="1000"
              max="100000"
              step="500"
              value={sqft}
              data-testid="sqft-slider"
              onChange={(e) => setSqft(e.target.value)}
              className="w-full accent-[#1c4f9c] cursor-pointer"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-2">
              <span>1,000</span>
              <span>100,000</span>
            </div>

            <button
              type="button"
              data-testid="supplies-toggle"
              onClick={() => setIncludeSupplies((v) => !v)}
              className={`mt-8 w-full flex items-start gap-4 text-left p-4 rounded-sm border-2 transition-colors duration-200 ${
                includeSupplies ? "border-[#1c4f9c] bg-[#1c4f9c]/5" : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <span className={`mt-0.5 w-6 h-6 shrink-0 flex items-center justify-center rounded-sm border-2 transition-colors ${includeSupplies ? "bg-[#1c4f9c] border-[#1c4f9c]" : "border-slate-300 bg-white"}`}>
                {includeSupplies && <Check size={16} className="text-white" strokeWidth={3} />}
              </span>
              <span>
                <span className="block text-sm font-bold text-[#1c4f9c]">Include Cleaning Supplies</span>
                <span className="block text-xs text-slate-500 mt-0.5">We provide & restock paper products, soap, trash liners and cleaning products each month, based on your restrooms & dispensers.</span>
              </span>
            </button>

            {includeSupplies && (
              <div data-testid="supplies-inputs" className="mt-4 grid sm:grid-cols-2 gap-4">
                <Stepper label="Number of Restrooms" value={bathrooms} setValue={setBathrooms} testid="bathrooms" />
                <Stepper label="Soap / Paper Dispensers" value={dispensers} setValue={setDispensers} testid="dispensers" />
              </div>
            )}
          </div>

          {/* Result */}
          <div className="lg:col-span-2 bg-[#1c4f9c] rounded-sm p-8 sm:p-10 text-white flex flex-col justify-between relative overflow-hidden">
            <div className="absolute inset-0 grain-overlay opacity-10" />
            <div className="relative">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#ff6b70]">Estimated Monthly Cost</p>
              <div className="mt-4 flex items-end gap-2">
                {loading ? (
                  <Loader2 className="animate-spin text-white/70" size={40} />
                ) : (
                  <span data-testid="estimate-total" className="text-5xl sm:text-6xl font-extrabold font-display leading-none">
                    ${result ? Number(result.estimated_monthly).toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
                  </span>
                )}
                <span className="text-blue-200 mb-1">/mo</span>
              </div>
              {result && (
                <div className="mt-6 space-y-2 text-sm text-blue-100/90 border-t border-white/15 pt-5">
                  <div className="flex justify-between"><span>Per visit</span><span className="font-semibold">${Number(result.per_visit).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>Visits / month</span><span className="font-semibold">{result.visits_per_month}</span></div>
                  {result.include_supplies && (
                    <div className="flex justify-between" data-testid="supplies-line"><span>Supplies ({result.bathrooms} restrooms, {result.dispensers} dispensers)</span><span className="font-semibold">${Number(result.supplies_cost).toLocaleString()}</span></div>
                  )}
                  <div className="flex justify-between"><span>Service frequency</span><span className="font-semibold capitalize">{result.frequency}</span></div>
                </div>
              )}
            </div>
            <a
              href="#contact"
              data-testid="request-quote-btn"
              onClick={() => {
                if (!result) return;
                const freqLabel = FREQUENCIES.find((f) => f.id === result.frequency)?.label || result.frequency;
                const suppliesTxt = result.include_supplies
                  ? ` Supplies included (${result.bathrooms} restrooms, ${result.dispensers} dispensers).`
                  : " No supplies needed.";
                const summary = `I'd like a final quote. Facility: ${Number(result.square_footage).toLocaleString()} sq ft, ${freqLabel}.${suppliesTxt} Estimated monthly: $${Number(result.estimated_monthly).toLocaleString()}.`;
                window.dispatchEvent(new CustomEvent("prefill-quote", {
                  detail: {
                    message: summary,
                    frequency: result.frequency,
                    square_footage: result.square_footage,
                    estimated_monthly: result.estimated_monthly,
                  },
                }));
              }}
              className="relative mt-8 inline-flex items-center justify-center gap-2 bg-[#e02329] text-white font-bold py-4 px-6 rounded-sm hover:bg-[#c81e24] transition-colors duration-200"
            >
              Request Final Quote <ArrowRight size={18} strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
